/**
 * Mind 宿主节点检索，以及 `/` 技能列表。
 * 数据来自页面上的 window.MindHost，不是 HTTP。
 * 宿主不存在或 version 不是 1 时返回 null，调用方应关掉列表，不要抛错。
 */

import { t, translationVariants } from '@/i18n';

export const MIND_SEARCH_LIMIT = 20;
/** 与宿主默认一致。旧实现曾截成 10，逐级浏览时会把子级裁掉。 */
export const MIND_LIST_LIMIT = 50;
export const MIND_SEARCH_DEBOUNCE_MS = 250;

export type MindNodeType = 'doc_space' | 'project' | 'folder' | 'doc' | 'file';

export interface MindNodeHit {
  id: string;
  type: MindNodeType;
  spaceId: string;
  name: string;
  /** 从所在空间名到父级目录名，不含节点自己。空间和项目为空数组。 */
  path: string[];
  /**
   * 能否按 → 进入直接子级。旧宿主没有该字段，按 false 处理，不请求下级。
   * 只用于列表浏览，不写入对话文本。
   */
  hasChildren?: boolean;
  /**
   * 能否选进输入框。只有明确 false 才禁止选中，旧宿主没有该字段时仍可选。
   * 只用于列表，不写入对话文本。
   */
  selectable?: boolean;
}

/** 传给 listEntries 的父节点。 */
export interface MindNodeRef {
  type: MindNodeType;
  id: string;
  spaceId: string;
  name?: string;
  path?: string[];
}

export interface MindNodeSearchResult {
  items: MindNodeHit[];
  partial: boolean;
}

/** listSkills 的一条。只保留契约字段。 */
export interface MindSkill {
  id: string;
  name: string;
}

export interface MindSkillListResult {
  skills: MindSkill[];
  partial: boolean;
}

export interface MindHostSaveDocumentResult {
  resourceId: string;
  docSpaceId: string;
  parentId: string | null;
  title: string;
}

export interface MindHostApi {
  version: number;
  searchNodes(keyword: string, options?: { limit?: number }): Promise<MindNodeSearchResult>;
  /**
   * 不传 parent：当前页的一级入口。传 parent：该节点的直接子级。
   * 旧宿主可能没有这个方法，或忽略 parent 且条目没有 hasChildren。
   */
  listEntries?(options?: { parent?: MindNodeRef; limit?: number }): Promise<MindNodeSearchResult>;
  /**
   * 把回答交给 Mind 存成文档。version 仍是 1，旧宿主没有这个方法。
   * null 表示用户关闭或页面卸载。
   */
  saveDocument?(input: { markdown: string; title?: string }): Promise<MindHostSaveDocumentResult | null>;
  /**
   * 当前可用的 skill。旧宿主没有这个方法。
   * partial 为 true 且 skills 为空表示这次列表不可用。
   */
  listSkills?(): Promise<MindSkillListResult>;
}

declare global {
  interface Window {
    MindHost?: MindHostApi;
  }
}

const MIND_NODE_TYPES = new Set<MindNodeType>(['doc_space', 'project', 'folder', 'doc', 'file']);

export type MindMentionPart =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; hit: MindNodeHit }
  | { kind: 'skill'; skill: MindSkill };

/** init.source 为 Mind 时启用 @ 和 /。大小写不敏感。 */
export function isMindSource(source: string | undefined | null): boolean {
  return (source ?? '').trim().toLowerCase() === 'mind';
}

export function isMindNodeHit(value: unknown): value is MindNodeHit {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === 'string' &&
    record.id.length > 0 &&
    typeof record.spaceId === 'string' &&
    record.spaceId.length > 0 &&
    typeof record.name === 'string' &&
    typeof record.type === 'string' &&
    MIND_NODE_TYPES.has(record.type as MindNodeType)
  );
}

function readPath(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

/** 列表里跟在名称后面的路径，例如「产品空间 / 2026 规划」。 */
export function mindNodePathLabel(path: string[] | undefined): string {
  return readPath(path).join(' / ');
}

/** 进入下级后的面包屑：parent.path + parent.name。 */
export function mindNodeBreadcrumb(hit: Pick<MindNodeHit, 'path' | 'name'>): string {
  const parts = readPath(hit.path);
  const name = hit.name.trim();
  if (name) parts.push(name);
  return parts.join(' / ');
}

/** 旧宿主没有 hasChildren，只有严格 true 才允许请求下级。 */
export function mindNodeHasChildren(hit: MindNodeHit): boolean {
  return hit.hasChildren === true;
}

/** 只有明确 false 才不能选中。缺字段的旧数据仍可选。 */
export function mindNodeSelectable(hit: MindNodeHit): boolean {
  return hit.selectable !== false;
}

export function toMindNodeRef(hit: MindNodeHit): MindNodeRef {
  return {
    type: hit.type,
    id: hit.id,
    spaceId: hit.spaceId,
    name: hit.name,
    path: readPath(hit.path),
  };
}

/** 只保留契约字段，避免把宿主多出来的字段带给对话接口。缺 path 时按空数组。 */
export function canonicalMindNodeHit(hit: MindNodeHit): MindNodeHit {
  return {
    id: hit.id,
    type: hit.type,
    spaceId: hit.spaceId,
    name: hit.name,
    path: readPath(hit.path),
  };
}

/** 发给对话接口的片段：@ 加上 MindNodeHit JSON，不能只传名称。 */
export function formatMindMention(hit: MindNodeHit): string {
  return `@${JSON.stringify(canonicalMindNodeHit(hit))}`;
}

export function isMindSkill(value: unknown): value is MindSkill {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === 'string' &&
    record.id.length > 0 &&
    typeof record.name === 'string' &&
    record.name.length > 0
  );
}

/** 只保留 id、name，避免把宿主多出来的字段带给对话接口。 */
export function canonicalMindSkill(skill: MindSkill): MindSkill {
  return { id: skill.id, name: skill.name };
}

const SKILL_TOKEN_KEY = 'chatInput.skillToken';

/** 发给对话接口的片段：当前语言的「使用SKILL」加上 skill JSON，不能只传名称。 */
export function formatMindSkill(skill: MindSkill): string {
  return `${t(SKILL_TOKEN_KEY)}${JSON.stringify(canonicalMindSkill(skill))}`;
}

/** 已拉取的列表按名称过滤。空关键字返回全部。 */
export function filterMindSkills(skills: MindSkill[], keyword: string): MindSkill[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return skills;
  return skills.filter((skill) => skill.name.toLowerCase().includes(query));
}

/** 旧宿主没有 saveDocument。每次读取，不缓存。 */
export function canSaveMindDocument(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const host = window.MindHost;
    return Boolean(host && host.version === 1 && typeof host.saveDocument === 'function');
  } catch {
    return false;
  }
}

export type MindSaveOutcome = 'saved' | 'cancelled' | 'invalid' | 'busy' | 'failed';

function mindErrorCode(error: unknown): string {
  if (!error || typeof error !== 'object' || !('code' in error)) return '';
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

/** 把回答 markdown 交给宿主。用户取消时返回 cancelled，不抛错。 */
export async function saveMindDocument(markdown: string): Promise<MindSaveOutcome> {
  const text = markdown.trim();
  if (!text) return 'invalid';
  let host: MindHostApi | undefined;
  try {
    host = window.MindHost;
  } catch (error) {
    console.error('[Macaw] 读取 MindHost 失败', error);
    return 'failed';
  }
  if (!host || host.version !== 1 || typeof host.saveDocument !== 'function') return 'failed';
  try {
    const result = await host.saveDocument({ markdown: text });
    return result ? 'saved' : 'cancelled';
  } catch (error) {
    const code = mindErrorCode(error);
    if (code === 'invalid') return 'invalid';
    if (code === 'busy') return 'busy';
    console.error('[Macaw] 保存回答失败', error);
    return 'failed';
  }
}

export function getMindHost(): MindHostApi | null {
  try {
    if (typeof window === 'undefined') return null;
    const host = window.MindHost;
    if (!host || host.version !== 1 || typeof host.searchNodes !== 'function') return null;
    return host;
  } catch (error) {
    console.error('[Macaw] @ 读取 MindHost 失败', error);
    return null;
  }
}

/** listSkills 不可用时返回原因；可用时返回 null。只给控制台诊断用。 */
export function describeMindSkillGap(): string | null {
  try {
    if (typeof window === 'undefined') return 'window 不可用';
    const host = window.MindHost;
    if (!host) return 'window.MindHost 不存在';
    if (host.version !== 1) return `version=${String(host.version)}，需要 1`;
    if (typeof host.listSkills !== 'function') return 'listSkills 不是函数';
    return null;
  } catch {
    return '读取 window.MindHost 抛错';
  }
}

function inspectMindSkillResult(value: unknown): { result: MindSkillListResult; rawCount: number | null } {
  if (!value || typeof value !== 'object') {
    return { result: { skills: [], partial: true }, rawCount: null };
  }
  const record = value as { skills?: unknown; partial?: unknown };
  if (!Array.isArray(record.skills)) {
    return { result: { skills: [], partial: Boolean(record.partial) }, rawCount: null };
  }
  return {
    result: {
      skills: record.skills.filter(isMindSkill).map((skill) => canonicalMindSkill(skill)),
      partial: Boolean(record.partial),
    },
    rawCount: record.skills.length,
  };
}

/**
 * 拉取 skill。宿主没有 listSkills 时返回 null（`/` 保持普通字符）。
 * 调用失败时返回 partial: true，Promise 本身不 reject。
 */
export async function listMindSkills(): Promise<MindSkillListResult | null> {
  if (describeMindSkillGap()) return null;
  let host: MindHostApi;
  try {
    host = window.MindHost as MindHostApi;
  } catch (error) {
    console.error('[Macaw] / 读取 MindHost 失败', error);
    return null;
  }
  if (typeof host.listSkills !== 'function') return null;
  try {
    const value = await host.listSkills();
    const inspected = inspectMindSkillResult(value);
    if (inspected.result.skills.length === 0) {
      console.warn('[Macaw] / listSkills 返回空列表', {
        partial: inspected.result.partial,
        rawCount: inspected.rawCount,
      });
    }
    return inspected.result;
  } catch (error) {
    console.error('[Macaw] / listSkills 失败', error);
    return { skills: [], partial: true };
  }
}

/** 宿主不可用时返回原因；可用时返回 null。只给控制台诊断用。 */
export function describeMindHostGap(): string | null {
  try {
    if (typeof window === 'undefined') return 'window 不可用';
    const host = window.MindHost;
    if (!host) return 'window.MindHost 不存在';
    if (host.version !== 1) return `version=${String(host.version)}，需要 1`;
    if (typeof host.searchNodes !== 'function') return 'searchNodes 不是函数';
    return null;
  } catch {
    return '读取 window.MindHost 抛错';
  }
}

interface InspectedMindSearch {
  result: MindNodeSearchResult;
  rawCount: number | null;
  invalidItemKeys?: string[];
}

function inspectMindSearchResult(value: unknown, limit: number): InspectedMindSearch {
  if (!value || typeof value !== 'object') {
    return { result: { items: [], partial: true }, rawCount: null };
  }
  const record = value as { items?: unknown; partial?: unknown };
  if (!Array.isArray(record.items)) {
    return { result: { items: [], partial: Boolean(record.partial) }, rawCount: null };
  }
  const valid = record.items.filter(isMindNodeHit);
  const invalid = record.items.find((item) => !isMindNodeHit(item));
  return {
    result: {
      items: valid.map((item) => {
        const hit = canonicalMindNodeHit(item);
        if (item.hasChildren === true) hit.hasChildren = true;
        if (item.selectable === false) hit.selectable = false;
        return hit;
      }).slice(0, limit),
      partial: Boolean(record.partial),
    },
    rawCount: record.items.length,
    invalidItemKeys: invalid === undefined
      ? undefined
      : invalid && typeof invalid === 'object'
        ? Object.keys(invalid)
        : ['(not an object)'],
  };
}

function logEmptyMindList(api: 'listEntries' | 'searchNodes', inspected: InspectedMindSearch, keyword?: string): void {
  if (inspected.result.items.length > 0) return;
  console.warn(`[Macaw] @ ${api} 返回空列表`, {
    ...(keyword !== undefined ? { keyword } : {}),
    partial: inspected.result.partial,
    rawCount: inspected.rawCount,
    validCount: 0,
    ...(inspected.rawCount && inspected.invalidItemKeys ? { invalidItemKeys: inspected.invalidItemKeys } : {}),
  });
}

/**
 * 检索节点。宿主不可用时返回 null（关闭 @，不向用户抛错）。
 * 宿主可用但失败时返回 partial: true，Promise 本身不 reject，原因打到控制台。
 */
export async function searchMindNodes(keyword: string): Promise<MindNodeSearchResult | null> {
  const host = getMindHost();
  if (!host) return null;
  const trimmed = keyword.trim();
  if (!trimmed) return { items: [], partial: false };
  try {
    const value = await host.searchNodes(trimmed, { limit: MIND_SEARCH_LIMIT });
    const inspected = inspectMindSearchResult(value, MIND_SEARCH_LIMIT);
    logEmptyMindList('searchNodes', inspected, trimmed);
    return inspected.result;
  } catch (error) {
    console.error('[Macaw] @ searchNodes 失败', { keyword: trimmed, error });
    return { items: [], partial: true };
  }
}

/**
 * 不传 parent：一级入口。传 parent：该节点的直接子级。
 * 只在 hasChildren === true 时带 parent，避免旧宿主忽略 parent 后把一级列表当成子级。
 * 宿主没有 listEntries 时返回 null，调用方保持「请输入名称」提示，不向用户抛错。
 */
export async function listMindEntries(parent?: MindNodeHit): Promise<MindNodeSearchResult | null> {
  const host = getMindHost();
  if (!host || typeof host.listEntries !== 'function') return null;
  if (parent && !mindNodeHasChildren(parent)) return { items: [], partial: false };
  try {
    const value = await host.listEntries({
      limit: MIND_LIST_LIMIT,
      ...(parent ? { parent: toMindNodeRef(parent) } : {}),
    });
    const inspected = inspectMindSearchResult(value, MIND_LIST_LIMIT);
    logEmptyMindList('listEntries', inspected);
    return inspected.result;
  } catch (error) {
    console.error('[Macaw] @ listEntries 失败', error);
    return { items: [], partial: true };
  }
}

function findJsonObjectEnd(text: string, start: number): number {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i += 1) {
    const char = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (char === '\\') {
        escaped = true;
        continue;
      }
      if (char === '"') inString = false;
      continue;
    }
    if (char === '"') {
      inString = true;
      continue;
    }
    if (char === '{') depth += 1;
    else if (char === '}') {
      depth -= 1;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}

/** 把接口文本里的 @MindNodeHit 和「使用SKILL」JSON 拆开，供气泡渲染。 */
export function parseMindMentions(text: string): MindMentionPart[] {
  const parts: MindMentionPart[] = [];
  let buffer = '';
  let i = 0;

  const flush = () => {
    if (!buffer) return;
    parts.push({ kind: 'text', text: buffer });
    buffer = '';
  };

  const skillPrefixes = translationVariants(SKILL_TOKEN_KEY);

  while (i < text.length) {
    const skillPrefix = skillPrefixes.find((prefix) => (
      text.startsWith(prefix, i) && text[i + prefix.length] === '{'
    ));
    if (skillPrefix) {
      const jsonAt = i + skillPrefix.length;
      const end = findJsonObjectEnd(text, jsonAt);
      if (end > jsonAt) {
        try {
          const parsed: unknown = JSON.parse(text.slice(jsonAt, end));
          if (isMindSkill(parsed)) {
            flush();
            parts.push({ kind: 'skill', skill: canonicalMindSkill(parsed) });
            i = end;
            continue;
          }
        } catch {
          // 不是合法技能，按普通字符继续
        }
      }
    }
    if (text[i] === '@' && text[i + 1] === '{') {
      const end = findJsonObjectEnd(text, i + 1);
      if (end > i + 1) {
        try {
          const parsed: unknown = JSON.parse(text.slice(i + 1, end));
          if (isMindNodeHit(parsed)) {
            flush();
            parts.push({ kind: 'mention', hit: canonicalMindNodeHit(parsed) });
            i = end;
            continue;
          }
        } catch {
          // 不是合法引用，按普通字符继续
        }
      }
    }
    if (text[i] === '/' && text[i + 1] === '{') {
      const end = findJsonObjectEnd(text, i + 1);
      if (end > i + 1) {
        try {
          const parsed: unknown = JSON.parse(text.slice(i + 1, end));
          if (isMindSkill(parsed)) {
            flush();
            parts.push({ kind: 'skill', skill: canonicalMindSkill(parsed) });
            i = end;
            continue;
          }
        } catch {
          // 旧格式不是合法技能，按普通字符继续
        }
      }
    }
    buffer += text[i];
    i += 1;
  }

  flush();
  return parts;
}

/** 历史标题等只读展示：文件引用为 @名称，技能为 /名称。 */
export function mindMentionDisplayText(text: string): string {
  return parseMindMentions(text)
    .map((part) => {
      if (part.kind === 'text') return part.text;
      if (part.kind === 'mention') return `@${part.hit.name || part.hit.id}`;
      return `/${part.skill.name || part.skill.id}`;
    })
    .join('');
}
